import { useEffect, useRef, useState, useCallback } from "react";

interface ParallaxOptions {
  speed?: number;
  direction?: "up" | "down";
  disabled?: boolean;
}

const isMobile = typeof window !== "undefined" && window.innerWidth < 768;

export const useParallax = ({ 
  speed = 0.5, 
  direction = "up",
  disabled = false 
}: ParallaxOptions = {}) => {
  const elementRef = useRef<HTMLDivElement>(null);
  const [offset, setOffset] = useState(0);
  const rafRef = useRef<number | null>(null);
  const lastOffsetRef = useRef(0);

  const updateOffset = useCallback(() => {
    if (!elementRef.current || isMobile) return;

    const rect = elementRef.current.getBoundingClientRect();
    const scrollPercent = (window.innerHeight - rect.top) / (window.innerHeight + rect.height);
    
    if (scrollPercent >= 0 && scrollPercent <= 1) {
      const movement = scrollPercent * 100 * speed;
      const newOffset = direction === "up" ? -movement : movement;
      
      if (Math.abs(newOffset - lastOffsetRef.current) > 0.5) {
        lastOffsetRef.current = newOffset;
        setOffset(newOffset);
      }
    }
  }, [speed, direction]);

  useEffect(() => {
    if (disabled || isMobile) return;

    const handleScroll = () => {
      if (rafRef.current) {
        cancelAnimationFrame(rafRef.current);
      }
      rafRef.current = requestAnimationFrame(updateOffset);
    };

    updateOffset();
    window.addEventListener("scroll", handleScroll, { passive: true });
    
    return () => {
      window.removeEventListener("scroll", handleScroll);
      if (rafRef.current) {
        cancelAnimationFrame(rafRef.current);
      }
    };
  }, [disabled, updateOffset]);

  if (isMobile) {
    return { ref: elementRef, style: {} };
  }

  return { 
    ref: elementRef, 
    style: { 
      transform: `translateY(${offset}px)`,
      willChange: "transform"
    } 
  };
};
